// # Subscription Management Component for Admin
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../../../auth/state';
import { 
  collection, 
  query, 
  getDocs,
  writeBatch,
  doc,
  serverTimestamp
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../../../firebase/config';
import SharedHeader from '../../../../components/SharedHeader';
import DashboardFooter from '../../../../components/DashboardFooter';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Button,
  CircularProgress,
  Typography,
  Grid,
  List,
  ListItem,
  ListItemText,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  InputAdornment,
  TextField,
  Chip,
  Tabs,
  Tab
} from '@mui/material';
import { Edit, Add, ExtensionRounded, Block, Search, Refresh } from '@mui/icons-material';
import { FaUsers } from 'react-icons/fa'; // # Import user management icon

// # Import dialog components
import EditDialog from './dialogs/edit';
import GiveDialog from './dialogs/give';
import ExtendDialog from './dialogs/extend';
import DeactivateDialog from './dialogs/deactivate';
import AssignMentorDialog from './dialogs/assign_mentor';

// # Import subscription types and constants
import { SUBSCRIPTION_TYPES, SUBSCRIPTION_STATUS } from './dialogs/constants';

import './subscription_management.css';

// # Add this component for subscription status
const SubscriptionStatus = ({ status }) => {
  const getStatusClass = () => {
    switch (status) {
      case SUBSCRIPTION_STATUS.ACTIVE: return 'sbsc_mng_status-active';
      case SUBSCRIPTION_STATUS.INACTIVE: return 'sbsc_mng_status-inactive';
      case SUBSCRIPTION_STATUS.EXPIRED: return 'sbsc_mng_status-expired';
      case SUBSCRIPTION_STATUS.PENDING: return 'sbsc_mng_status-pending';
      default: return '';
    }
  };

  return (
    <span className={`sbsc_mng_subscription-status ${getStatusClass()}`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
};

// # Add helper function to calculate subscription status
const calculateStatus = (startDate, endDate) => {
  if (!startDate || !endDate) return 'inactive';
  
  const now = new Date();
  const start = new Date(startDate);
  const end = new Date(endDate);

  if (now < start) return 'pending';
  if (now > end) return 'expired';
  return 'active';
};

// # Add helper function for batch updates
const updateSubscriptionBatch = async (db, uid, subscriptionType, updateData) => {
  const batch = writeBatch(db);
  const subscriptionRef = doc(db, 'subscriptions', uid);
  
  // # Prepare the update object with the nested path
  const update = {
    [`subscriptions.${subscriptionType}`]: {
      ...updateData,
      metadata: {
        lastEditedAt: serverTimestamp(),
        lastEditedBy: 'admin'
      }
    }
  };
  
  batch.update(subscriptionRef, update);
  
  // # Commit the batch
  await batch.commit();
  console.log(`Batch update completed for user ${uid}, subscription ${subscriptionType}`);
  return true;
};

const SubscriptionManagement = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuthStore();
  const [subscriptions, setSubscriptions] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedSubscriptionType, setSelectedSubscriptionType] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [actionType, setActionType] = useState('');
  const [formData, setFormData] = useState({
    subscriptionType: '',
    startDate: '',
    endDate: '',
    mentor: '',
    duration: '1',
    durationUnit: 'minutes'
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [selectedUserReport, setSelectedUserReport] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [currentTab, setCurrentTab] = useState('all');

  // # Redirect if not admin
  useEffect(() => {
    if (!isAdmin) {
      navigate('/dashboard');
    }
  }, [isAdmin, navigate]);

  // # Add fetchSubscriptions function
  const fetchSubscriptions = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      console.log('Starting subscription data fetch:', {
        timestamp: new Date().toISOString(),
        operation: 'fetch_subscriptions'
      });
      
      const startTime = performance.now();
      
      // # Get all users with better error handling
      const usersQuery = query(collection(db, 'users'));
      const usersSnapshot = await getDocs(usersQuery).catch(error => {
        console.error('Error fetching users:', error);
        throw new Error('Failed to fetch users data');
      });
      
      console.log('Users fetch completed:', {
        count: usersSnapshot.size,
        timeElapsed: `${(performance.now() - startTime).toFixed(2)}ms`
      });
      
      // # Get all subscriptions with better error handling
      const subsStartTime = performance.now();
      const subsQuery = query(collection(db, 'subscriptions'));
      const subsSnapshot = await getDocs(subsQuery).catch(error => {
        console.error('Error fetching subscriptions:', error);
        throw new Error('Failed to fetch subscription data');
      });
      
      console.log('Subscriptions fetch completed:', {
        count: subsSnapshot.size,
        timeElapsed: `${(performance.now() - subsStartTime).toFixed(2)}ms`
      });
      
      // # Create subscription lookup map with logging
      const subscriptionMap = {};
      subsSnapshot.forEach((doc) => {
        subscriptionMap[doc.id] = doc.data();
      });
      
      // # Combine user and subscription data with validation
      const userData = usersSnapshot.docs.map(doc => {
        const user = doc.data();
        const subscription = subscriptionMap[doc.id];
        
        // # Create default subscription structure if missing
        const defaultSubscription = {
          subscriptions: Object.keys(SUBSCRIPTION_TYPES).reduce((acc, key) => {
            const type = SUBSCRIPTION_TYPES[key];
            acc[type.id] = {
              status: 'inactive',
              startDate: 'none',
              endDate: 'none',
              ...(type.requiresMentor && { mentor: 'none' })
            };
            return acc;
          }, {})
        };

        // # Merge existing subscription data with defaults
        const mergedSubscription = subscription || defaultSubscription;
        
        return {
          uid: doc.id,
          email: user.email || 'No email',
          userName: `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Unknown User',
          photoURL: user.photoURL || null,
          subscriptions: mergedSubscription.subscriptions || defaultSubscription.subscriptions
        };
      });
      
      const totalTime = performance.now() - startTime;
      console.log('Total subscription data fetch completed:', {
        users: userData.length,
        subscriptions: Object.keys(subscriptionMap).length,
        totalTimeElapsed: `${totalTime.toFixed(2)}ms`,
        timestamp: new Date().toISOString()
      });
      
      setSubscriptions(userData);
    } catch (error) {
      console.error('Error in fetchSubscriptions:', error);
      setError(`Failed to fetch data: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // # Update the useEffect hook to use fetchSubscriptions
  useEffect(() => {
    if (!isAdmin) return;
    fetchSubscriptions();
  }, [isAdmin]);

  // # Handle action click
  const handleActionClick = (user, subscriptionType, action) => {
    setSelectedUser(user);
    setSelectedSubscriptionType(subscriptionType);
    setActionType(action);
    
    // # Initialize form data based on action type
    if (action === 'edit') {
      // # For edit, use current subscription values
      const currentSubscription = user.subscriptions[subscriptionType];
      setFormData({
        subscriptionType: SUBSCRIPTION_TYPES[subscriptionType.toUpperCase()].name,
        startDate: currentSubscription?.startDate || new Date().toISOString(),
        endDate: currentSubscription?.endDate || new Date().toISOString(),
        mentor: currentSubscription?.mentor || null,
        duration: '1',
        durationUnit: 'months'
      });
    } else {
      // # For other actions, use default values
      setFormData({
        subscriptionType: SUBSCRIPTION_TYPES[subscriptionType.toUpperCase()].name,
        startDate: new Date().toISOString(),
        endDate: new Date().toISOString(),
        mentor: '',
        duration: '1',
        durationUnit: 'minutes'  // Default to minutes
      });
    }
    
    setDialogOpen(true);
  };

  // # Handle dialog submission
  const handleDialogSubmit = (dialogData) => {
    console.log('Dialog submitted with data:', dialogData); // Add logging
    
    // # For payment-related operations (give, extend), use Cloud Functions
    if (actionType === 'give') {
      // # Call the Cloud Function directly with dialog data
      const handleGiveSubmit = async () => {
        try {
          console.log('Submitting give action with data:', dialogData); // Add logging
          const giveFunction = httpsCallable(functions, 'updateSubscription');
          const result = await giveFunction({
            uid: selectedUser.uid,
            action: 'give',
            subscriptionType: selectedSubscriptionType,
            duration: parseInt(dialogData.duration),
            durationUnit: dialogData.durationUnit,
            mentor: dialogData.mentor,
            metadata: {
              manuallyEdited: true,
              editedBy: 'admin',
              editedAt: new Date().toISOString()
            }
          });

          if (!result.data.success) {
            throw new Error(result.data.message || 'Failed to create subscription');
          }

          setSnackbar({
            open: true,
            message: `Subscription created for ${dialogData.duration} ${dialogData.durationUnit}`,
            severity: 'success'
          });

          await fetchSubscriptions();
          setDialogOpen(false);
        } catch (error) {
          console.error('Error updating subscription:', error);
          setSnackbar({
            open: true,
            message: `Error updating subscription: ${error.message}`,
            severity: 'error'
          });
        }
      };

      handleGiveSubmit();
    } else {
      // # For administrative operations (edit, deactivate, assign mentor), use batch writes
      handleAdminSubmit(dialogData);
    }
  };

  // # Add new function for handling administrative operations with batch writes
  const handleAdminSubmit = async (dialogData) => {
    try {
      console.log(`Processing ${actionType} with batch write:`, dialogData);
      
      // # For assign_mentor action, the dialogData is already prepared in the dialog component
      if (actionType === 'assign_mentor') {
        // # Use batch write to update only the mentor field
        const batch = writeBatch(db);
        const subscriptionRef = doc(db, 'subscriptions', selectedUser.uid);
        
        // # Only update the mentor field, preserving all other subscription data
        batch.update(subscriptionRef, {
          [`subscriptions.${selectedSubscriptionType}.mentor`]: dialogData.mentor
        });
        
        // # Commit the batch write
        await batch.commit();
        
        // # Log the mentor assignment
        const logSubscription = httpsCallable(functions, 'logSubscription');
        await logSubscription({
          uid: selectedUser.uid,
          subscriptionType: selectedSubscriptionType,
          action: 'assign_mentor',
          newMentor: dialogData.mentor.uid
        });
        
        // # Call updateSubscription Cloud Function to update the mentor document
        try {
          const updateSubscriptionFunc = httpsCallable(functions, 'updateSubscription');
          await updateSubscriptionFunc({
            uid: selectedUser.uid,
            subscriptionType: selectedSubscriptionType,
            action: 'edit',
            mentor: dialogData.mentor
          });
          console.log('Called updateSubscription to update mentor document');
        } catch (error) {
          console.error('Error calling updateSubscription:', error);
        }
      } 
      // # For deactivate action, the dialogData is already prepared in the dialog component
      else if (actionType === 'deactivate') {
        await updateSubscriptionBatch(db, selectedUser.uid, selectedSubscriptionType, dialogData);
        
        // # Get current subscription data for logging
        const currentSubscription = selectedUser.subscriptions[selectedSubscriptionType];
        
        // # Log the deactivation
        const logSubscription = httpsCallable(functions, 'logSubscription');
        await logSubscription({
          uid: selectedUser.uid,
          subscriptionType: selectedSubscriptionType,
          action: 'deactivate',
          // # Metadata for deactivate action
          startDate: currentSubscription?.startDate || null,
          oldEndDate: currentSubscription?.endDate || null,
          endDate: dialogData.endDate || null
        });
      }
      // # For edit action, the dialogData is already prepared in the dialog component
      else if (actionType === 'edit') {
        // # Ensure the status is included in the update
        if (!dialogData.status && dialogData.startDate && dialogData.endDate) {
          dialogData.status = calculateStatus(dialogData.startDate, dialogData.endDate);
        }
        
        await updateSubscriptionBatch(db, selectedUser.uid, selectedSubscriptionType, dialogData);
        
        // # Get current subscription data for logging
        const currentSubscription = selectedUser.subscriptions[selectedSubscriptionType];
        
        // # Determine what has changed for logging
        const changes = {};
        
        // # Check if start date has changed
        if (dialogData.startDate && dialogData.startDate !== currentSubscription.startDate) {
          changes.oldStartDate = currentSubscription.startDate;
          changes.startDate = dialogData.startDate;
        }
        
        // # Check if end date has changed
        if (dialogData.endDate && dialogData.endDate !== currentSubscription.endDate) {
          changes.oldEndDate = currentSubscription.endDate;
          changes.endDate = dialogData.endDate;
        }
        
        // # Check if status has changed
        if (dialogData.status && dialogData.status !== currentSubscription.status) {
          changes.oldStatus = currentSubscription.status;
          changes.newStatus = dialogData.status;
        }
        
        // # Check if mentor has changed
        const oldMentorUid = currentSubscription?.mentor?.uid || null;
        const newMentorUid = dialogData.mentor?.uid || null;
        if (oldMentorUid !== newMentorUid) {
          changes.oldMentor = oldMentorUid;
          changes.newMentor = newMentorUid;
          
          // # Call updateSubscription Cloud Function to update the mentor document
          if (newMentorUid) {
            try {
              const updateSubscriptionFunc = httpsCallable(functions, 'updateSubscription');
              await updateSubscriptionFunc({
                uid: selectedUser.uid,
                subscriptionType: selectedSubscriptionType,
                action: 'edit',
                mentor: dialogData.mentor,
                startDate: dialogData.startDate || currentSubscription.startDate,
                endDate: dialogData.endDate || currentSubscription.endDate
              });
              console.log('Called updateSubscription to update mentor document');
            } catch (error) {
              console.error('Error calling updateSubscription:', error);
            }
          }
        }
        
        // # Log the edit only if something has changed
        if (Object.keys(changes).length > 0) {
          const logSubscription = httpsCallable(functions, 'logSubscription');
          await logSubscription({
            uid: selectedUser.uid,
            subscriptionType: selectedSubscriptionType,
            action: 'edit',
            // # Include only the fields that have changed
            ...changes
          });
        }
      }
      
      setSnackbar({
        open: true,
        message: `Subscription ${actionType === 'deactivate' ? 'deactivated' : 'updated'} successfully`,
        severity: 'success'
      });
      
      await fetchSubscriptions();
      setDialogOpen(false);
    } catch (error) {
      console.error(`Error in ${actionType} operation:`, error);
      setSnackbar({
        open: true,
        message: `Error updating subscription: ${error.message}`,
        severity: 'error'
      });
    }
  };

  // # Update the handleSubmit function
  const handleSubmit = async () => {
    try {
      const now = new Date().toISOString();

      // # For extend action, continue using Cloud Functions
      if (actionType === 'extend') {
        const currentSubscription = selectedUser.subscriptions[selectedSubscriptionType];
        if (!currentSubscription) {
          throw new Error('Subscription not found');
        }

        // # Call the Cloud Function for extend
        const extendFunction = httpsCallable(functions, 'updateSubscription');
        const result = await extendFunction({
          uid: selectedUser.uid,
          action: 'extend',
          subscriptionType: selectedSubscriptionType,
          duration: parseInt(formData.duration),
          durationUnit: formData.durationUnit
        });

        if (!result.data.success) {
          throw new Error(result.data.message || 'Failed to extend subscription');
        }

        // # Calculate the new end date for logging
        const currentEndDate = new Date(currentSubscription.endDate);
        let newEndDate;
        
        switch(formData.durationUnit) {
          case 'minutes':
            newEndDate = new Date(currentEndDate.getTime() + (parseInt(formData.duration) * 60 * 1000));
            break;
          case 'days':
            newEndDate = new Date(currentEndDate.getTime() + (parseInt(formData.duration) * 24 * 60 * 60 * 1000));
            break;
          case 'weeks':
            newEndDate = new Date(currentEndDate.getTime() + (parseInt(formData.duration) * 7 * 24 * 60 * 60 * 1000));
            break;
          case 'months':
            newEndDate = new Date(currentEndDate);
            newEndDate.setMonth(newEndDate.getMonth() + parseInt(formData.duration));
            break;
          default:
            newEndDate = currentEndDate;
        }
        
        // # Log the extension
        const logSubscription = httpsCallable(functions, 'logSubscription');
        await logSubscription({
          uid: selectedUser.uid,
          subscriptionType: selectedSubscriptionType,
          action: 'extend',
          // # Metadata for extend action
          startDate: currentSubscription.startDate,
          oldEndDate: currentSubscription.endDate,
          endDate: newEndDate.toISOString(),
          // # Additional metadata
          duration: parseInt(formData.duration),
          durationUnit: formData.durationUnit
        });

        setSnackbar({
          open: true,
          message: `Subscription extended for ${formData.duration} ${formData.durationUnit}`,
          severity: 'success'
        });
      } 
      // # For other actions, pass the formData to handleDialogSubmit
      else {
        handleDialogSubmit(formData);
        return; // Early return to avoid duplicate dialog closing
      }

      await fetchSubscriptions();
      setDialogOpen(false);
      
    } catch (error) {
      console.error('Error updating subscription:', error);
      setSnackbar({
        open: true,
        message: `Error updating subscription: ${error.message}`,
        severity: 'error'
      });
    }
  };

  // # Add filter logic for tabs
  const getFilteredSubscriptions = () => {
    let filtered = [...subscriptions];
    
    // First apply search filter
    if (searchTerm) {
      filtered = filtered.filter((sub) => {
        const searchLower = searchTerm.toLowerCase();
        return (
          sub.userName.toLowerCase().includes(searchLower) ||
          sub.email.toLowerCase().includes(searchLower)
        );
      });
    }
    
    // Then apply tab filter
    switch (currentTab) {
      case 'active':
        return filtered.filter(user => 
          Object.values(user.subscriptions || {}).some(sub => sub?.status === 'active')
        );
      case 'no_subscription':
        return filtered.filter(user => 
          !Object.values(user.subscriptions || {}).some(sub => sub?.status === 'active')
        );
      case 'ending':
        const tenDaysFromNow = new Date();
        tenDaysFromNow.setDate(tenDaysFromNow.getDate() + 10);
        const now = new Date();
        return filtered.filter(user =>
          Object.values(user.subscriptions || {}).some(sub => 
            sub?.status === 'active' && 
            new Date(sub.endDate) <= tenDaysFromNow &&
            new Date(sub.endDate) > now
          )
        );
      case 'expired':
        return filtered.filter(user =>
          Object.values(user.subscriptions || {}).some(sub => 
            sub?.status === SUBSCRIPTION_STATUS.EXPIRED ||
            (sub?.endDate && sub.endDate !== 'none' && new Date(sub.endDate) < new Date())
          )
        );
      default:
        return filtered;
    }
  };

  // # Handle tab change
  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  // # Add refresh function
  const handleRefreshSubscriptions = async () => {
    try {
      setIsRefreshing(true);
      
      // # Call the Cloud Function
      const manualCheckFunction = httpsCallable(functions, 'manualCheckSubscriptions');
      const result = await manualCheckFunction();
      
      if (result.data.success) {
        setSnackbar({
          open: true,
          message: result.data.message || 'Subscriptions refreshed successfully',
          severity: 'success'
        });
        
        await fetchSubscriptions();
      } else {
        throw new Error(result.data.error || 'Failed to refresh subscriptions');
      }
    } catch (error) {
      console.error('Error refreshing subscriptions:', error);
      setSnackbar({
        open: true,
        message: `Error refreshing subscriptions: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  // # Add helper function to format mentor display
  const getMentorDisplay = (mentor) => {
    if (!mentor) return 'None';
    if (mentor === 'none') return 'None';
    if (typeof mentor === 'string') return mentor;
    if (typeof mentor === 'object' && mentor.name) {
      const roleDisplay = mentor.displayRole || mentor.role || '';
      return `${mentor.name} (${mentor.email}) - ${roleDisplay}`;
    }
    return 'None';
  };

  // # Render subscription row
  const renderSubscriptionRow = (user) => {
    if (!user) return null;

    // # Count active subscriptions safely
    const activeSubscriptions = Object.entries(user.subscriptions || {})
      .filter(([_, sub]) => sub?.status === 'active')
      .length;

    return (
      <TableRow 
        key={user.uid}
        sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#f5f5f5' } }}
        onClick={() => {
          setSelectedUserReport(user);
          setReportDialogOpen(true);
        }}
      >
        <TableCell>
          <Avatar 
            src={user.photoURL} 
            className="sbsc_mng_user-avatar"
          />
        </TableCell>
        <TableCell>{user.userName || 'Unknown User'}</TableCell>
        <TableCell>{user.email || 'No email'}</TableCell>
        <TableCell align="center">
          <Chip 
            label={`${activeSubscriptions} Active`}
            color={activeSubscriptions > 0 ? "success" : "default"}
          />
        </TableCell>
      </TableRow>
    );
  };

  return (
    <div className="sbsc_mng_admin-page">
      <SharedHeader />
      
      <main className="sbsc_mng_admin-content">
        <div className="sbsc_mng_admin-header">
          <button 
            className="sbsc_mng_back-button"
            onClick={() => navigate('/admin')}
          >
            <i className="fas fa-arrow-left"></i> Back to Dashboard
          </button>
          <h1>Subscription Management</h1>
          <div 
            className="sbsc_mng_user-nav"
            onClick={() => navigate('/admin/users/management')}
            title="Go to User Management"
          >
            <FaUsers />
          </div>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<Refresh />}
            onClick={handleRefreshSubscriptions}
            disabled={isRefreshing}
            sx={{ ml: 'auto' }}
          >
            {isRefreshing ? 'Checking...' : 'Check Expired Subscriptions'}
          </Button>
        </div>

        {error ? (
          <div className="sbsc_mng_error-container">
            <p>{error}</p>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={() => window.location.reload()}
            >
              Retry
            </Button>
          </div>
        ) : isLoading ? (
          <div className="sbsc_mng_loading-container">
            <CircularProgress />
            <p>Loading users...</p>
          </div>
        ) : subscriptions.length === 0 ? (
          <div className="sbsc_mng_no-data-container">
            <p>No users found.</p>
          </div>
        ) : (
          <>
            <Paper sx={{ mb: 2 }}>
              <Tabs
                value={currentTab}
                onChange={handleTabChange}
                indicatorColor="primary"
                textColor="primary"
                className="sbsc_mng_tabs"
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{
                  '.MuiTabs-scrollButtons': {
                    '&.Mui-disabled': { opacity: 0.3 },
                  },
                }}
              >
                <Tab value="all" label={`All (${subscriptions.length})`} />
                <Tab 
                  value="active" 
                  label={`Active (${subscriptions.filter(user => 
                    Object.values(user.subscriptions || {}).some(sub => sub?.status === 'active')
                  ).length})`}
                />
                <Tab 
                  value="no_subscription" 
                  label={`No Subscriptions (${subscriptions.filter(user => 
                    !Object.values(user.subscriptions || {}).some(sub => sub?.status === 'active')
                  ).length})`}
                />
                <Tab 
                  value="ending" 
                  label={`Ending Soon (${subscriptions.filter(user => {
                    const thirtyDaysFromNow = new Date();
                    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 10);
                    return Object.values(user.subscriptions || {}).some(sub => 
                      sub?.status === 'active' && 
                      new Date(sub.endDate) <= thirtyDaysFromNow &&
                      new Date(sub.endDate) > new Date()
                    );
                  }).length})`}
                />
                <Tab 
                  value="expired" 
                  label={`Expired (${subscriptions.filter(user =>
                    Object.values(user.subscriptions || {}).some(sub => 
                      sub?.status === SUBSCRIPTION_STATUS.EXPIRED ||
                      (sub?.endDate && sub.endDate !== 'none' && new Date(sub.endDate) < new Date())
                    )
                  ).length})`}
                />
              </Tabs>
            </Paper>

            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ mb: 3 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
            
            <TableContainer component={Paper} sx={{ 
              width: '100%',
              overflowX: 'auto',
              WebkitOverflowScrolling: 'touch',
              position: 'relative',
              '& .MuiTable-root': {
                minWidth: { xs: '800px', sm: '1000px' }
              },
              '& .MuiTableCell-root': {
                whiteSpace: { xs: 'nowrap', sm: 'normal' },
                padding: { xs: '12px', sm: '16px' }
              },
              '@media (max-width: 768px)': {
                margin: '1rem -0.5rem',
                width: 'calc(100% + 1rem) !important',
                borderRadius: 0,
                boxShadow: 'none'
              }
            }}>
              <Table aria-label="subscription table">
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell align="center">Active Subscriptions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getFilteredSubscriptions().map(user => renderSubscriptionRow(user))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {/* Update Report Dialog */}
        <Dialog 
          open={reportDialogOpen} 
          onClose={() => setReportDialogOpen(false)}
          maxWidth="md"
          fullWidth
          sx={{
            '& .MuiDialog-paper': {
              margin: { xs: '16px', sm: '24px' },
              width: { 
                xs: 'calc(100% - 32px) !important',
                sm: '100%'
              },
              maxHeight: {
                xs: 'calc(100% - 32px)',
                sm: '90vh'
              }
            }
          }}
        >
          <DialogTitle>
            <div className="sbsc_mng_user-header">
              <button 
                className="sbsc_mng_modal-close" 
                onClick={() => setReportDialogOpen(false)}
              >
                ×
              </button>
              <Avatar 
                src={selectedUserReport?.photoURL} 
                className="sbsc_mng_modal-avatar"
                sx={{
                  width: { xs: 45, sm: 60 },
                  height: { xs: 45, sm: 60 }
                }}
              />
              <div className="sbsc_mng_user-info">
                <Typography variant="h6">
                  {selectedUserReport?.userName || 'Unknown User'}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {selectedUserReport?.email || 'No email'}
                </Typography>
              </div>
            </div>
          </DialogTitle>
          <DialogContent>
            {selectedUserReport && (
              <Grid container spacing={2}>
                {Object.entries(SUBSCRIPTION_TYPES).map(([key, type]) => {
                  const subscription = selectedUserReport.subscriptions?.[type.id] || {
                    status: 'inactive',
                    startDate: 'none',
                    endDate: 'none',
                    ...(type.requiresMentor && { mentor: 'none' })
                  };

                  return (
                    <Grid item xs={12} md={6} key={type.id}>
                      <Paper sx={{ p: 2 }} className="sbsc_mng_subscription-box">
                        <Typography variant="subtitle1" gutterBottom>
                          {type.name}
                        </Typography>
                        <List dense>
                          <ListItem>
                            <ListItemText 
                              primary="Status" 
                              secondary={<SubscriptionStatus status={subscription.status || 'inactive'} />} 
                            />
                          </ListItem>
                          <ListItem>
                            <ListItemText 
                              primary="Start Date" 
                              secondary={subscription.startDate === 'none' ? 'N/A' : new Date(subscription.startDate).toLocaleDateString() + ' | ' + new Date(subscription.startDate).toLocaleTimeString()} 
                            />
                          </ListItem>
                          <ListItem>
                            <ListItemText 
                              primary="End Date" 
                              secondary={subscription.endDate === 'none' ? 'N/A' : new Date(subscription.endDate).toLocaleDateString() + ' | ' + new Date(subscription.endDate).toLocaleTimeString()} 
                            />
                          </ListItem>
                          {type.requiresMentor && (
                            <ListItem>
                              <ListItemText 
                                primary="Mentor" 
                                secondary={getMentorDisplay(subscription.mentor)}
                              />
                            </ListItem>
                          )}
                        </List>
                        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                          <Button
                            startIcon={<Edit />}
                            size="small"
                            onClick={() => {
                              setReportDialogOpen(false);
                              handleActionClick(selectedUserReport, type.id, 'edit');
                            }}
                          >
                            Edit
                          </Button>
                          <Button
                            startIcon={<ExtensionRounded />}
                            size="small"
                            onClick={() => {
                              setReportDialogOpen(false);
                              handleActionClick(selectedUserReport, type.id, 'extend');
                            }}
                          >
                            Extend
                          </Button>
                          {type.requiresMentor && (
                            <Button
                              startIcon={<Edit />}
                              size="small"
                              color="primary"
                              onClick={() => {
                                setReportDialogOpen(false);
                                handleActionClick(selectedUserReport, type.id, 'assign_mentor');
                              }}
                            >
                              Assign Mentor
                            </Button>
                          )}
                          {subscription.status === 'active' ? (
                            <Button
                              startIcon={<Block />}
                              size="small"
                              color="error"
                              onClick={() => {
                                setReportDialogOpen(false);
                                handleActionClick(selectedUserReport, type.id, 'deactivate');
                              }}
                            >
                              Deactivate
                            </Button>
                          ) : (
                            <Button
                              startIcon={<Add />}
                              size="small"
                              color="success"
                              onClick={() => {
                                setReportDialogOpen(false);
                                handleActionClick(selectedUserReport, type.id, 'give');
                              }}
                            >
                              Activate
                            </Button>
                          )}
                        </Stack>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </DialogContent>
        </Dialog>

        {/* Render action dialogs */}
        {dialogOpen && (
          <>
            {actionType === 'edit' && (
              <EditDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                onSubmit={handleSubmit}
                formData={formData}
                setFormData={setFormData}
                selectedUser={selectedUser}
                selectedSubscriptionType={selectedSubscriptionType}
                subscriptionTypes={SUBSCRIPTION_TYPES}
              />
            )}
            {actionType === 'give' && (
              <GiveDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                onSubmit={handleDialogSubmit}
                formData={formData}
                setFormData={setFormData}
                selectedUser={selectedUser}
                selectedSubscriptionType={selectedSubscriptionType}
              />
            )}
            {actionType === 'extend' && (
              <ExtendDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                onSubmit={handleSubmit}
                selectedUser={selectedUser}
                selectedSubscriptionType={selectedSubscriptionType}
                formData={formData}
                setFormData={setFormData}
              />
            )}
            {actionType === 'deactivate' && (
              <DeactivateDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                onSubmit={handleSubmit}
                selectedUser={selectedUser}
                selectedSubscriptionType={selectedSubscriptionType}
              />
            )}
            {actionType === 'assign_mentor' && (
              <AssignMentorDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                onSubmit={handleSubmit}
                selectedUser={selectedUser}
                selectedSubscriptionType={selectedSubscriptionType}
              />
            )}
          </>
        )}
      </main>

      <DashboardFooter />
    </div>
  );
};

export default SubscriptionManagement; 